import React, { useState } from 'react';
import { List } from 'immutable';

import Tabs from 'flow/components/ui-components/Tabs';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectHasCalendarEditAccess } from 'flow/selectors/own-extension-selectors';
import { Calendar } from 'flow/types/Calendar';
import {
    ChannelKey,
    ContactKey,
    ExtensionKey,
    QueueKey,
    ReferKey,
} from 'flow/types/EntityKeys';
import { Profile } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';
import { isExtensionKey } from 'flow/utils';
import {
    isExternallySyncedCalendar,
    setSameAsMonday,
} from 'flow/utils/calendar-utils';

import CalendarExternallyManagedInfo from './CalendarExternallyManagedInfo';
import CalendarSettings from './CalendarSettings';
import CalendarWeek from './CalendarWeek';

interface Props {
    calendar: TypedMap<Calendar>;
    profiles: List<TypedMap<Profile>>;
    entityKey: ChannelKey | ContactKey | ExtensionKey | QueueKey | ReferKey;
    view?: 'graphical' | 'list';
    showExternallyManagedInfo?: boolean;
    handleUpdateOpeningHours: (openingHours: object) => void;
    handleClearCalendar: () => void;
}

const CalendarMainView = ({
    calendar,
    profiles,
    entityKey,
    view = 'list',
    showExternallyManagedInfo = false,
    handleClearCalendar,
    handleUpdateOpeningHours,
}: Props) => {
    const { i18next } = useAppContext();
    const [viewState, setViewState] = useState(view);
    const hasEditAccess = useAppSelector((state) =>
        selectHasCalendarEditAccess(state, entityKey),
    );

    const handleUpdateMoToFri = () => {
        const openingHours = setSameAsMonday(
            calendar.get('openingHours'),
            calendar.getIn(['openingHours', 'monday']),
        )
            .set('saturday', calendar.getIn(['openingHours', 'saturday']))
            .set('sunday', calendar.getIn(['openingHours', 'sunday']));

        handleUpdateOpeningHours(openingHours);
    };

    const tabs = [
        {
            key: 'list',
            label: i18next.t('calendar.view.list'),
        },
        {
            key: 'graphical',
            label: i18next.t('calendar.view.graphical'),
        },
    ];

    return (
        <>
            <Tabs
                className="mb-6"
                tabs={tabs}
                tabClassName="p-3"
                onClick={({ key }: any) => setViewState(key)}
            />
            {showExternallyManagedInfo &&
            hasEditAccess &&
            isExternallySyncedCalendar(calendar) ? (
                <CalendarExternallyManagedInfo />
            ) : null}
            {viewState === 'list' && (
                <CalendarSettings
                    key={`calendar-settings-${entityKey}`}
                    calendar={calendar}
                    profiles={profiles}
                    isExtension={isExtensionKey(entityKey)}
                    handleUpdateMoToFri={handleUpdateMoToFri}
                    handleUpdateOpeningHours={handleUpdateOpeningHours}
                />
            )}

            {viewState === 'graphical' && (
                <CalendarWeek
                    entityKey={entityKey}
                    calendar={calendar}
                    profiles={profiles}
                    handleClearCalendar={handleClearCalendar}
                />
            )}
        </>
    );
};

export default CalendarMainView;
